/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

// ============================================================================
// Types
// ============================================================================

type Theme = "light" | "dark" | "system";

interface Toast {
  id: string;
  type: "success" | "error" | "warning" | "info";
  title: string;
  message?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface UIState {
  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;

  // Command palette (Cmd+K)
  commandPaletteOpen: boolean;

  // Theme
  theme: Theme;
  resolvedTheme: "light" | "dark";

  // Mobile
  isMobile: boolean;

  // Toasts
  toasts: Toast[];

  // Loading overlay
  globalLoading: boolean;
  globalLoadingMessage: string | null;

  // Actions - Sidebar
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapsed: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Actions - Command palette
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  toggleCommandPalette: () => void;

  // Actions - Theme
  setTheme: (theme: Theme) => void;

  // Actions - Mobile
  setIsMobile: (isMobile: boolean) => void;

  // Actions - Toast
  addToast: (toast: Omit<Toast, "id">) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Actions - Loading
  setGlobalLoading: (loading: boolean, message?: string) => void;
}

// ============================================================================
// Helpers
// ============================================================================

function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

function getResolvedTheme(theme: Theme): "light" | "dark" {
  if (theme === "system") {
    if (typeof window !== "undefined") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return "light";
  }
  return theme;
}

// ============================================================================
// Store
// ============================================================================

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Initial state
      sidebarOpen: true,
      sidebarCollapsed: false,
      commandPaletteOpen: false,
      theme: "system",
      resolvedTheme: (() => {
        const resolved = getResolvedTheme("system");
        if (typeof document !== "undefined") {
          const root = document.documentElement;
          root.classList.remove("light", "dark");
          root.classList.add(resolved);
          root.setAttribute("data-theme", resolved);
        }
        return resolved;
      })(),
      isMobile: false,
      toasts: [],
      globalLoading: false,
      globalLoadingMessage: null,

      // Sidebar actions
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      toggleSidebarCollapsed: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

      // Command palette actions
      openCommandPalette: () => set({ commandPaletteOpen: true }),

      closeCommandPalette: () => set({ commandPaletteOpen: false }),

      toggleCommandPalette: () =>
        set((state) => ({ commandPaletteOpen: !state.commandPaletteOpen })),

      // Theme actions
      setTheme: (theme) => {
        const resolved = getResolvedTheme(theme);
        set({
          theme,
          resolvedTheme: resolved,
        });
        if (typeof document !== "undefined") {
          const root = document.documentElement;
          root.classList.remove("light", "dark");
          root.classList.add(resolved);
          root.setAttribute("data-theme", resolved);
        }
      },

      // Mobile actions
      setIsMobile: (isMobile) => set({ isMobile }),

      // Toast actions
      addToast: (toast) => {
        const id = generateId();
        const duration = toast.duration ?? 5000;

        set((state) => ({
          toasts: [...state.toasts, { ...toast, id }],
        }));

        // Auto-remove toast after duration
        if (duration > 0) {
          setTimeout(() => {
            get().removeToast(id);
          }, duration);
        }
      },

      removeToast: (id) =>
        set((state) => ({
          toasts: state.toasts.filter((t) => t.id !== id),
        })),

      clearToasts: () => set({ toasts: [] }),

      // Loading actions
      setGlobalLoading: (loading, message) =>
        set({
          globalLoading: loading,
          globalLoadingMessage: loading ? (message ?? null) : null,
        }),
    }),
    {
      name: "torqe-ui",
      storage: createJSONStorage(() => localStorage), // UI prefs can use localStorage
      partialize: (state) => ({
        // Only persist user preferences
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          const resolved = getResolvedTheme(state.theme);
          state.resolvedTheme = resolved;
          if (typeof document !== "undefined") {
            const root = document.documentElement;
            root.classList.remove("light", "dark");
            root.classList.add(resolved);
            root.setAttribute("data-theme", resolved);
          }
        }
      },
    }
  )
);

// ============================================================================
// Fine-grained Selectors
// ============================================================================

// Sidebar
export const useSidebarOpen = () => useUIStore((s) => s.sidebarOpen);
export const useSidebarCollapsed = () => useUIStore((s) => s.sidebarCollapsed);

// Command palette
export const useCommandPaletteOpen = () => useUIStore((s) => s.commandPaletteOpen);

// Theme
export const useTheme = () => useUIStore((s) => s.theme);
export const useResolvedTheme = () => useUIStore((s) => s.resolvedTheme);
export const useIsDarkMode = () => useUIStore((s) => s.resolvedTheme === "dark");

// Mobile
export const useIsMobile = () => useUIStore((s) => s.isMobile);

// Toasts
export const useToasts = () => useUIStore((s) => s.toasts);

// Loading
export const useGlobalLoading = () =>
  useUIStore((s) => ({
    loading: s.globalLoading,
    message: s.globalLoadingMessage,
  }));

// ============================================================================
// Toast Helper Hooks
// ============================================================================

export function useToast() {
  const addToast = useUIStore((s) => s.addToast);
  const removeToast = useUIStore((s) => s.removeToast);

  return {
    toast: addToast,
    dismiss: removeToast,
    success: (title: string, message?: string) => {
      addToast({ type: "success", title, message });
    },
    error: (title: string, message?: string) => {
      addToast({ type: "error", title, message, duration: 10000 });
    },
    warning: (title: string, message?: string) => {
      addToast({ type: "warning", title, message });
    },
    info: (title: string, message?: string) => {
      addToast({ type: "info", title, message });
    },
  };
}

// ============================================================================
// Keyboard Shortcut Setup
// ============================================================================

export function setupKeyboardShortcuts() {
  if (typeof window === "undefined") return;

  const handleKeyDown = (e: KeyboardEvent) => {
    // Cmd/Ctrl + K -> Command palette
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault();
      useUIStore.getState().toggleCommandPalette();
    }

    // Escape -> Close command palette
    if (e.key === "Escape") {
      const state = useUIStore.getState();
      if (state.commandPaletteOpen) {
        state.closeCommandPalette();
      }
    }

    // Cmd/Ctrl + B -> Toggle sidebar
    if ((e.metaKey || e.ctrlKey) && e.key === "b") {
      e.preventDefault();
      useUIStore.getState().toggleSidebarCollapsed();
    }
  };

  window.addEventListener("keydown", handleKeyDown);
  return () => {
    window.removeEventListener("keydown", handleKeyDown);
  };
}
